import {Row, Col, Table, Dropdown, Menu} from 'antd';
import {ColumnsType} from 'antd/lib/table';
import {useContext, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import IntlMessages from '../../../../../domain/utility/IntlMessages';
import {
  CUSTOMER_ROLES,
  INDEX_PAGE_SIZE_DEFAULT,
  INDEX_PAGE_SIZE_OPTIONS,
  PAGE_NUMBER,
} from '../../../../../shared/constants/AppConst';
import {ordersService} from '../../../../../shared/services/orders';
import {OrderDetailsItemsDto} from '../../../../../shared/services/orders/dto/order-details-items-dto';
import MainUtils from 'src/shared/utils/main';
import {OrderDetailsDto} from 'src/shared/services/orders/dto/order-details-dto';
import StockBreakQuantityModal from './stock-break-quantity-modal';
import {AuthContext} from 'src/pages/auth/context';
import useTableV2 from 'src/shared/hooks/useTableV2';
import RowActionsButton from 'src/shared/components/row-actions';

interface IOrderDetailsItemsProps {
  orderDetails?: OrderDetailsDto;
}

const OrderDetailsItems = ({orderDetails}: IOrderDetailsItemsProps) => {
  const {id} = useParams();
  const {showTotal, ...tableV2} = useTableV2();
  const [orderDetailsItemsList, setOrderDetailsItemsList] = useState<
    OrderDetailsItemsDto[]
  >([]);
  const [isOrderDetailsItemsListRefeash, setIsOrderDetailsItemsListRefeash] =
    useState(false);
  const [isOrderDetailsItemsListLoading, setIsOrderDetailsItemsLoading] =
    useState(false);
  const [pageSizeOptions] = useState<string[]>(INDEX_PAGE_SIZE_OPTIONS);
  const [pagination, setPagination] = useState({
    Page: PAGE_NUMBER,
    PageSize: INDEX_PAGE_SIZE_DEFAULT,
  });
  const [ordersDetailsItemsTotal, setOrdersDetailsItemsTotal] = useState(0);

  const [setAsStockBreakModalVisibility, setSetAsStockBreakModalVisibility] =
    useState(false);
  const [orderItemDetails, setOrderItemDetails] =
    useState<OrderDetailsItemsDto>();

  const {userRolesName} = useContext(AuthContext);

  useEffect(() => {
    if (!MainUtils.isEmptyValue(id)) {
      (async () => {
        try {
          setIsOrderDetailsItemsLoading(true);
          let itemsDate = await ordersService.getOrderDetailsItems({
            orderId: id ?? '',
            Page: pagination?.Page,
            PageSize: pagination?.PageSize,
          });
          setOrderDetailsItemsList(itemsDate.Data);
          setOrdersDetailsItemsTotal(itemsDate.Total);
        } catch (error) {
          console.error(error);
        } finally {
          setIsOrderDetailsItemsLoading(false);
        }
      })();
    }
  }, [id, pagination, isOrderDetailsItemsListRefeash]);

  const onSetAsStockBreakModal = (record: OrderDetailsItemsDto) => {
    setSetAsStockBreakModalVisibility(true);
    setOrderItemDetails(record);
  };

  const onStockBreakQuantityModal = () => {
    return (
      <StockBreakQuantityModal
        visible={setAsStockBreakModalVisibility}
        orderDetails={orderDetails}
        orderDetailsItems={orderItemDetails}
        onCancel={() => setSetAsStockBreakModalVisibility(false)}
        onFinish={() => {
          setSetAsStockBreakModalVisibility(false);
          setIsOrderDetailsItemsListRefeash(!isOrderDetailsItemsListRefeash);
        }}
      />
    );
  };

  const columns: ColumnsType<any> = [
    {
      title: <IntlMessages id='orders.returnExchange.productName' />,
      dataIndex: 'ProductName',
      key: 'ProductName',
    },
    {
      title: <IntlMessages id='orders.orderDetails.orderItems.sku' />,
      dataIndex: 'Sku',
      key: 'Sku',
    },
    {
      title: <IntlMessages id='orders.orderDetails.orderItems.quantity' />,
      dataIndex: 'Quantity',
      key: 'Quantity',
      className: 'col-num',
    },
    {
      title: <IntlMessages id='orders.orderDetails.orderItems.unitPrice' />,
      dataIndex: 'UnitPrice',
      key: 'UnitPrice',
      className: 'col-num',
    },
    {
      title: <IntlMessages id='orders.orderDetails.orderItems.itemPrice' />,
      dataIndex: 'ItemPrice',
      key: 'ItemPrice',
      className: 'col-num',
    },
    {
      title: (
        <IntlMessages id='orders.orderDetails.orderItems.stockBreakQuantity' />
      ),
      dataIndex: 'StockBreakQuantity',
      key: 'StockBreakQuantity',
      className: 'col-num',
    },
    {
      title: <IntlMessages id='common.Action' />,
      className: 'col-actions',
      width: 80,
      render: (text: string, item: OrderDetailsItemsDto) => (
        <>
          {userRolesName?.includes(CUSTOMER_ROLES.Administrators) ? (
            !item.StockBreak && orderDetails?.IsAdmin ? (
              <div>
                <Dropdown
                  trigger={['click']}
                  overlay={() => (
                    <Menu>
                      <Menu.Item
                        onClick={() => {
                          onSetAsStockBreakModal(item);
                        }}
                        key={'setAsStockBreak'}>
                        <span className='margin-in-action'>
                          <IntlMessages id='orders.subOrders.setAsStockBreak' />
                        </span>
                      </Menu.Item>
                    </Menu>
                  )}
                  placement='bottomRight'>
                  <RowActionsButton />
                </Dropdown>
              </div>
            ) : (
              '-'
            )
          ) : item.StockBreak ? (
            '-'
          ) : (
            <div>
              <Dropdown
                trigger={['click']}
                overlay={() => (
                  <Menu>
                    <Menu.Item
                      onClick={() => {
                        onSetAsStockBreakModal(item);
                      }}
                      key={'setAsStockBreak'}>
                      <span className='margin-in-action'>
                        <IntlMessages id='orders.subOrders.setAsStockBreak' />
                      </span>
                    </Menu.Item>
                  </Menu>
                )}
                placement='bottomRight'>
                <RowActionsButton />
              </Dropdown>
            </div>
          )}
        </>
      ),
    },
  ];

  const tablePagination = {
    total: ordersDetailsItemsTotal,
    pageSizeOptions: pageSizeOptions,
    pageSize: pagination.PageSize,
    showSizeChanger: true,
    showTotal,
    current: pagination.Page,
    onShowSizeChange: async (current: any, PageSize: any) => {
      setPagination({
        Page: current,
        PageSize: PageSize,
      });
    },
    onChange: async (Page: any, PageSize: any) => {
      setPagination({
        Page: Page,
        PageSize: PageSize,
      });
    },
  };

  return (
    <Row gutter={[0, 20]}>
      <Col span={24}>
        <Table
          columns={columns}
          dataSource={orderDetailsItemsList}
          loading={isOrderDetailsItemsListLoading}
          rowKey={(record: OrderDetailsItemsDto) => record.Id.toString()}
          pagination={tablePagination}
          {...tableV2}
        />
      </Col>
      {onStockBreakQuantityModal()}
    </Row>
  );
};

export default OrderDetailsItems;
